/* import __COLOCATED_TEMPLATE__ from './phone-go-live.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { handoverConfigErrorForConfigType } from 'embercom/lib/operator/fin-voice/number-configuration-helpers';
import safeWindowOpen from 'embercom/lib/safe-window-open';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    ruleset: $TSFixMe;
    phoneNumberId?: string;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updatePercentageRollout: (
      phoneNumberId: string,
      rollOutPercentageIn: number | undefined,
      rollOutPercentageNin: number | undefined,
    ) => Promise<void>;
    hasContentReadyForFin: boolean;
    onPopoverInteraction?: () => void;
  };
}

export default class PhoneGoLive extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare appService: $TSFixMe;
  @service declare router: RouterService;

  @tracked percentageRolloutInHours: number | undefined;
  @tracked percentageRolloutNinHours: number | undefined;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.populateInitialPercentageRollouts();
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get phoneNumber() {
    if (!this.args.phoneNumberId) {
      return undefined;
    }

    return this.finVoiceConfigService.phoneNumbers[this.args.phoneNumberId];
  }

  get isSaving() {
    return Boolean(this.numberConfiguration?.isSaving);
  }

  get savingDisabled() {
    return !this.args.phoneNumberId || this.isSaving;
  }

  get shouldShowPaywall() {
    return this.finVoiceConfigService.shouldShowPaywall;
  }

  get ninHandoverError() {
    return this.getDisabledMessageForNumberConfiguration('nin');
  }

  get inHandoverError() {
    return this.getDisabledMessageForNumberConfiguration('in');
  }

  get contentPageUrl(): string {
    return this.router.urlFor('apps.app.automation.fin-ai-agent.setup', {
      queryParams: {
        activeTab: 'add-content',
      },
    });
  }
  get hasSavedPercentageRollout() {
    return Object.keys(this.numberConfiguration?.predicates || {}).some((key) =>
      ['in', 'nin', 'default'].includes(key),
    );
  }

  get isExternalProvider() {
    return this.phoneNumber?.isExternalProvider || false;
  }

  get isExternalEnabled() {
    return (this.percentageRolloutInHours ?? 0) > 0;
  }

  @action
  async toggleExternalEnabled() {
    if (this.savingDisabled || !this.args.phoneNumberId) {
      return;
    }

    let newPercentage = this.isExternalEnabled ? 0 : 100;
    this.percentageRolloutInHours = newPercentage;
    this.percentageRolloutNinHours = newPercentage;

    await this.args.updatePercentageRollout(this.args.phoneNumberId, newPercentage, newPercentage);
  }

  @action
  async updatePercentageRolloutIn(percentageRollout: number) {
    if (this.savingDisabled || !this.args.phoneNumberId) {
      return;
    }

    this.percentageRolloutInHours = percentageRollout;

    await this.args.updatePercentageRollout(
      this.args.phoneNumberId,
      this.percentageRolloutInHours,
      this.percentageRolloutNinHours,
    );
  }

  @action
  async updatePercentageRolloutNin(percentageRollout: number) {
    if (this.savingDisabled || !this.args.phoneNumberId) {
      return;
    }

    this.percentageRolloutNinHours = percentageRollout;

    await this.args.updatePercentageRollout(
      this.args.phoneNumberId,
      this.percentageRolloutInHours,
      this.percentageRolloutNinHours,
    );
  }

  @action
  getDisabledMessageForNumberConfiguration(comparison: 'in' | 'nin') {
    let handoverError = handoverConfigErrorForConfigType(
      this.numberConfiguration?.handoverConfiguration,
      comparison,
    );

    if (handoverError) {
      let label = handoverError.label ? this.intl.t(handoverError.label) : undefined;
      return this.intl.t(handoverError.error, { label });
    }

    if (!this.args.hasContentReadyForFin) {
      return this.intl.t(
        'operator.fin.setup.setup-and-go-live.chat-setup-warning.not-content-ready.description',
        {
          contentPageUrl: this.contentPageUrl,
          htmlSafe: 'true',
        },
      );
    }

    if (!this.appService.app.canUseExternalAi) {
      return this.intl.t('operator.fin.setup.setup-and-go-live.set-fin-live.automatic-opt-in', {
        htmlSafe: 'true',
      });
    }

    return undefined;
  }

  @action
  populateInitialPercentageRollouts() {
    let rolloutPercentageIn: number | undefined = 0;
    let rolloutPercentageNin: number | undefined = 0;

    if (this.hasSavedPercentageRollout) {
      rolloutPercentageIn =
        this.numberConfiguration?.predicates?.in?.percentageRollout ||
        this.numberConfiguration?.predicates?.default?.percentageRollout;

      rolloutPercentageNin =
        this.numberConfiguration?.predicates?.nin?.percentageRollout ||
        this.numberConfiguration?.predicates?.default?.percentageRollout;
    } else {
      rolloutPercentageIn = this.numberConfiguration?.predicates?.percentageRollout;
      rolloutPercentageNin = this.numberConfiguration?.predicates?.percentageRollout;
    }

    this.percentageRolloutInHours = rolloutPercentageIn || 0;
    this.percentageRolloutNinHours = rolloutPercentageNin || 0;
  }

  @action
  openHelpArticle() {
    safeWindowOpen(
      'https://www.intercom.com/help/en/articles/10697275-deploy-fin-ai-agent-over-phone',
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::PhoneGoLive': typeof PhoneGoLive;
  }
}
